/**
 * OpenAI Management API エンドポイント
 * シンプルなCRUD操作（プロジェクト、ユーザー、APIキー）
 */

import type { ApiResponse, Project, User, ProjectUser, ApiKey } from './types';
import { apiRequest } from './client';

/**
 * Admin API Key の有効性をテスト
 */
export const testApiKey = async (apiKey: string): Promise<ApiResponse<unknown>> => {
  return apiRequest('/users?limit=1', apiKey);
};

/**
 * プロジェクト一覧を取得
 */
export const getProjects = async (
  apiKey: string,
  includeArchived = false
): Promise<ApiResponse<{ object: string; data: Project[]; has_more?: boolean; last_id?: string }>> => {
  const projects: Project[] = [];
  let after: string | undefined;
  const cursors = new Set<string>();

  while (true) {
    const params = new URLSearchParams({
      limit: '100',
      include_archived: includeArchived.toString(),
      ...(after && { after })
    });
    const response = await apiRequest(`/projects?${params}`, apiKey) as ApiResponse<{
      object: string;
      data: Project[];
      has_more?: boolean;
      last_id?: string;
    }>;
    if (!response.success || !response.data?.data) return response;

    const page = response.data.data;
    projects.push(...page);
    const hasMore = response.data.has_more ?? page.length === 100;
    if (!hasMore) break;
    if (page.length === 0) return { success: false, error: 'Project pagination returned an incomplete empty page' };

    const nextAfter = response.data.last_id ?? page[page.length - 1].id;
    if (!nextAfter || cursors.has(nextAfter) || cursors.size >= 1000) {
      return { success: false, error: 'Project pagination did not complete' };
    }
    cursors.add(nextAfter);
    after = nextAfter;
  }

  return { success: true, data: { object: 'list', data: projects, has_more: false } };
};

/**
 * 組織のユーザー一覧を取得
 */
export const getUsers = async (
  apiKey: string
): Promise<ApiResponse<{ object: string; data: User[] }>> => {
  return apiRequest('/users?limit=100', apiKey);
};

/**
 * プロジェクトのユーザー一覧を取得
 */
export const getProjectUsers = async (
  apiKey: string,
  projectId: string
): Promise<ApiResponse<{ object: string; data: ProjectUser[] }>> => {
  return apiRequest(`/projects/${projectId}/users?limit=100`, apiKey);
};

/**
 * プロジェクトのAPIキー一覧を取得（全件取得）
 */
export const getProjectApiKeys = async (
  apiKey: string,
  projectId: string
): Promise<ApiResponse<{ object: string; data: ApiKey[] }>> => {
  const allApiKeys: ApiKey[] = [];
  let after: string | undefined;
  let hasMore = true;
  const cursors = new Set<string>();

  while (hasMore) {
    const params = new URLSearchParams({
      limit: '100',
      ...(after && { after })
    });

    const response = await apiRequest(`/projects/${projectId}/api_keys?${params}`, apiKey) as ApiResponse<{
      object: string; data: ApiKey[]; has_more?: boolean; last_id?: string
    }>;

    if (!response.success || !response.data?.data) {
      return {
        success: false,
        error: response.error || 'Failed to fetch API keys',
        data: { object: 'list', data: [] }
      };
    }

    allApiKeys.push(...response.data.data);

    // next_cursor や has_more フィールドをチェック
    const hasMoreData = response.data.has_more ?? response.data.data.length === 100;
    if (hasMoreData) {
      const nextAfter = response.data.last_id ?? response.data.data[response.data.data.length - 1]?.id;
      if (!nextAfter || cursors.has(nextAfter) || cursors.size >= 1000) {
        return { success: false, error: `API key pagination did not complete for project ${projectId}` };
      }
      cursors.add(nextAfter);
      after = nextAfter;
    } else {
      hasMore = false;
    }
  }

  return {
    success: true,
    data: {
      object: 'list',
      data: allApiKeys
    }
  };
};

/**
 * プロジェクトのAPIキー一覧を取得（単一ページ）
 */
export const getProjectApiKeysPage = async (
  apiKey: string,
  projectId: string,
  limit = 100,
  after?: string
): Promise<ApiResponse<{ object: string; data: ApiKey[] }>> => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    ...(after && { after })
  });

  return apiRequest(`/projects/${projectId}/api_keys?${params}`, apiKey);
};

/**
 * 管理APIキー一覧を取得
 */
export const getAdminApiKeys = async (
  apiKey: string
): Promise<ApiResponse<{ object: string; data: ApiKey[] }>> => {
  return apiRequest('/admin_api_keys?limit=100', apiKey);
};
